import { RiArrowDownSLine, RiCloseLine } from 'react-icons/ri';
import { useEffect, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { PRICE_FILTER_MAX } from '@/constants/priceFilterRanges';
import { getMinMaxPriceRange } from '@/utils/formatting';
import {
  DOWN_ARROW_STYLE,
  PRICE_FILTER_PILL_TEXT,
} from './FilterBar.constants';
import styles from './FilterBar.module.css';
import type {
  ExtendedFilterBarProps,
  PriceFilterTags,
} from './FilterBar.types';

const FilterBar = ({
  filters,
  priceFilter,
  onFilterClick,
  onPriceFilterChange,
  isSaleSelected = false,
  onSaleToggle,
}: ExtendedFilterBarProps): JSX.Element => {
  const [displayPriceBottomsheet, setDisplayPriceBottomsheet] =
    useState<boolean>(false);
  const [priceFilterState, setPriceFilterState] =
    useState<PriceFilterTags[]>(priceFilter);

  useEffect(() => {
    setPriceFilterState(priceFilter);
  }, [priceFilter]);

  const isPriceFilterApplied = priceFilter.reduce((val, range) => {
    return val || range.isSelected;
  }, false);

  const getLabelFromRange = (
    minPrice: number,
    maxPrice: number | null
  ): string => {
    return maxPrice === PRICE_FILTER_MAX
      ? `₹${minPrice}+`
      : `₹${minPrice} - ₹${maxPrice}`;
  };

  const getPriceFilterDisplayString = (): string => {
    const { minPrice, maxPrice } = getMinMaxPriceRange(priceFilter);
    return getLabelFromRange(minPrice, maxPrice);
  };

  const handlePriceFilterClick = (): void => {
    setDisplayPriceBottomsheet(true);
  };

  const handleCloseBottomsheet = (): void => {
    setDisplayPriceBottomsheet(false);
  };

  const handlePriceLabelClick = (index: number): void => {
    setPriceFilterState((prev) => {
      const newState = [...prev];
      newState[index] = {
        ...newState[index],
        isSelected: !newState[index].isSelected,
      };
      return newState;
    });
  };

  const handleApply = (): void => {
    onPriceFilterChange(priceFilterState);
    handleCloseBottomsheet();
  };

  const handleCloseBottomsheetWithoutApply = (): void => {
    setPriceFilterState(priceFilter);
    handleCloseBottomsheet();
  };

  const renderPortal = (): JSX.Element | null => {
    const rootElement = document.getElementById('root');
    if (!rootElement) return null;

    return createPortal(
      <>
        <div
          className={`${styles.overlay} no-drag`}
          onClick={handleCloseBottomsheetWithoutApply}
        />
        <div className={`${styles.priceBottomsheet} no-drag`}>
          <div className={styles.optionButton}>
            {priceFilterState.map((priceRange, index) => {
              return (
                <button
                  key={index}
                  className={`${styles.labels} ${priceRange.isSelected ? styles.selectedLabel : ''}`}
                  onClick={() => handlePriceLabelClick(index)}
                >
                  {getLabelFromRange(priceRange.min, priceRange.max)}
                </button>
              );
            })}
            <button className={styles.buttonApply} onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </>,
      rootElement
    );
  };

  return (
    <div className={`${styles.container}`}>
      <button
        className={`${styles.filter} ${isPriceFilterApplied ? styles.selectedFilter : ''}`}
        onClick={handlePriceFilterClick}
      >
        {isPriceFilterApplied
          ? getPriceFilterDisplayString()
          : PRICE_FILTER_PILL_TEXT}
        <RiArrowDownSLine
          size={DOWN_ARROW_STYLE.size}
          color={DOWN_ARROW_STYLE.color}
        />
      </button>
      <button
        className={`${styles.filter} ${isSaleSelected ? styles.selectedFilter : ''}`}
        onClick={onSaleToggle}
      >
        On Sale
        {isSaleSelected && <RiCloseLine size={16} />}
      </button>
      {filters.map((filter, index) => (
        <button
          key={index}
          className={`${styles.filter} ${filter.isSelected ? styles.selectedFilter : ''}`}
          onClick={() => onFilterClick(filter.name)}
        >
          {filter.name}
        </button>
      ))}

      {displayPriceBottomsheet && renderPortal()}
    </div>
  );
};

export default FilterBar;
